import { useState, useEffect, useCallback } from "react";
import { forceRound } from "helpers/function";
import typeCompteService from "services/typeCompteService";

export default function useTypeCompteData(initialItemsPerPage = 10) {
  const [loading, setLoading] = useState(true);
  const [typeCompte, setTypeCompte] = useState([]);
  const [pagination, setPagination] = useState({
    numberPerPage: initialItemsPerPage,
    activeNumberPage: 1,
    numberPage: 0,
    showList: [],
    filteredList: [],
    isSearching: false,
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await typeCompteService.fetchAccountTypes();
      setTypeCompte(data || []);

      const numberPage = forceRound(data.length / pagination.numberPerPage);
      const showList = data.slice(0, pagination.numberPerPage);

      setPagination((prev) => ({
        ...prev,
        numberPage,
        showList,
        filteredList: data,
      }));
    } catch (error) {
      console.error("Error fetching account types:", error);
    } finally {
      setLoading(false);
    }
  }, [pagination.numberPerPage]);

  const getEndByPosition = useCallback(
    (position) => {
      let end = pagination.numberPerPage;
      for (let i = 1; i < position; i++) {
        end = end + pagination.numberPerPage;
      }
      return end;
    },
    [pagination.numberPerPage]
  );

  const handleSearch = useCallback(
    (searchValue) => {
      if (searchValue) {
        const searchTerm = searchValue.toLowerCase();
        const filtered = typeCompte.filter(
          (item) =>
            (item.name?.fr || "").toLowerCase().includes(searchTerm) ||
            (item.description?.fr || "").toLowerCase().includes(searchTerm)
        );

        setPagination((prev) => ({
          ...prev,
          isSearching: true,
          filteredList: filtered,
        }));
      } else {
        setPagination((prev) => ({
          ...prev,
          isSearching: false,
          activeNumberPage: 1,
          filteredList: typeCompte,
          showList: typeCompte.slice(0, pagination.numberPerPage),
        }));
      }
    },
    [typeCompte, pagination.numberPerPage]
  );

  const handlePerPageChange = useCallback(
    (newPerPage) => {
      const numberPerPage = parseInt(newPerPage);
      setPagination((prev) => ({
        ...prev,
        numberPerPage,
        activeNumberPage: 1,
        numberPage: forceRound(typeCompte.length / numberPerPage),
        showList: typeCompte.slice(0, numberPerPage),
      }));
    },
    [typeCompte]
  );

  const handlePageClick = useCallback(
    (e, page) => {
      e.preventDefault();
      const start = getEndByPosition(page) - pagination.numberPerPage;
      const end = getEndByPosition(page);

      setPagination((prev) => ({
        ...prev,
        activeNumberPage: page,
        showList: typeCompte.slice(start, end),
      }));
    },
    [typeCompte, pagination.numberPerPage, getEndByPosition]
  );

  const handleNextPage = useCallback(
    (e) => {
      e.preventDefault();
      if (pagination.activeNumberPage < pagination.numberPage) {
        const nextPage = pagination.activeNumberPage + 1;
        const start = getEndByPosition(nextPage) - pagination.numberPerPage;
        const end = getEndByPosition(nextPage);

        setPagination((prev) => ({
          ...prev,
          activeNumberPage: nextPage,
          showList: typeCompte.slice(start, end),
        }));
      }
    },
    [typeCompte, pagination, getEndByPosition]
  );

  const handlePreviousPage = useCallback(
    (e) => {
      e.preventDefault();
      if (pagination.activeNumberPage > 1) {
        const prevPage = pagination.activeNumberPage - 1;
        const start = getEndByPosition(prevPage) - pagination.numberPerPage;
        const end = getEndByPosition(prevPage);

        setPagination((prev) => ({
          ...prev,
          activeNumberPage: prevPage,
          showList: typeCompte.slice(start, end),
        }));
      }
    },
    [typeCompte, pagination, getEndByPosition]
  );

  const handleDelete = useCallback(
    async (typeId, index) => {
      try {
        await typeCompteService.deleteAccountType(typeId);

        const newTypeCompte = [...typeCompte];
        newTypeCompte.splice(index, 1);
        setTypeCompte(newTypeCompte);

        const numberPage = forceRound(
          newTypeCompte.length / pagination.numberPerPage
        );

        if (
          pagination.showList.length <= 1 &&
          pagination.activeNumberPage > 1
        ) {
          const prevPage = pagination.activeNumberPage - 1;
          const start = getEndByPosition(prevPage) - pagination.numberPerPage;
          const end = getEndByPosition(prevPage);

          setPagination((prev) => ({
            ...prev,
            activeNumberPage: prevPage,
            numberPage,
            showList: newTypeCompte.slice(start, end),
            filteredList: newTypeCompte,
          }));
        } else {
          const start =
            getEndByPosition(pagination.activeNumberPage) -
            pagination.numberPerPage;
          const end = getEndByPosition(pagination.activeNumberPage);

          setPagination((prev) => ({
            ...prev,
            numberPage,
            showList: newTypeCompte.slice(start, end),
            filteredList: newTypeCompte,
          }));
        }

        return true;
      } catch (error) {
        return { error };
      }
    },
    [typeCompte, pagination, getEndByPosition]
  );

  const pageNumbers = useCallback(() => {
    const pages = [];
    for (let i = 0; i < pagination.numberPage; i++) {
      pages[i] = i + 1;
    }
    return pages;
  }, [pagination.numberPage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    loading,
    data: pagination.isSearching
      ? pagination.filteredList
      : pagination.showList,
    pagination: {
      perPage: pagination.numberPerPage,
      currentPage: pagination.activeNumberPage,
      totalPages: pagination.numberPage,
      isSearching: pagination.isSearching,
      pageNumbers: pageNumbers(),
    },
    handleSearch,
    handlePerPageChange,
    handlePageClick,
    handleNextPage,
    handlePreviousPage,
    handleDelete,
    totalItems: typeCompte.length,
  };
}
